import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { ReactComponent as ThanxAiSparkles } from 'assets/images/thanx-ai-sparkles.svg'
import { buildTranslate } from 'locales'
import React from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { buildConfigurePath } from './WizardStepper'

const t = buildTranslate('recovery_ai.wizard.quick_start')

// The intro preview is a decorative 3-step overview (per the Figma) — it is NOT
// the wizard's stepper stages, so it is defined here rather than sourced from
// STEPS.
const PREVIEW_STEPS = [
  { icon: 'fa-comment', titleKey: 'step1_title', descKey: 'step1_description' },
  { icon: 'fa-magic', titleKey: 'step2_title', descKey: 'step2_description' },
  { icon: 'fa-rocket', titleKey: 'step3_title', descKey: 'step3_description' },
] as const

const STEP_ICON_SIZE_PX = 48
// Shared by the icon row and the caption row so each icon centers directly
// above its title/description (both rows are three flex:1 columns with this
// gap between them).
const COLUMN_GAP_PX = 24

// The R12 onboarding intro (stage 0). A non-persisting landing rendered before
// the stepper flow; "Get started" enters the first wizard stage.
const QuickStart = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()

  return (
    <div
      className={css({
        backgroundColor: merchantTheme.colors.grey05,
        minHeight: '100vh',
        padding: '64px 24px',
      })}
    >
      <div
        className={`d-flex flex-column align-items-center text-center ${css({
          maxWidth: '960px',
          margin: '0 auto',
          gap: '24px',
        })}`}
      >
        {/* ThanxAI wordmark, with the ThanxAI sparkle mark (reused from the
            existing AI-prompt/segment-builder surfaces, see
            assets/images/thanx-ai-sparkles.svg) anchored at the top-right per
            the Figma. */}
        <span
          className={css({
            position: 'relative',
            display: 'inline-block',
          })}
        >
          <span
            className={css({
              fontFamily: 'Lato, sans-serif',
              fontWeight: 900,
              fontStyle: 'italic',
              fontSize: '28px',
              color: merchantTheme.colors.spearmint50,
            })}
          >
            {t('wordmark')}
          </span>
          <span
            data-testid="quick-start-sparkles"
            aria-hidden="true"
            className={css({
              position: 'absolute',
              top: '-10px',
              right: '-20px',
              width: '20px',
              height: '18px',
            })}
          >
            <ThanxAiSparkles
              className={css({ width: '100%', height: '100%' })}
            />
          </span>
        </span>

        <div className={`d-flex flex-column ${css({ gap: '16px' })}`}>
          <Text.Header3 bold>{t('headline')}</Text.Header3>
          <Text.BodyText3 color="grey70">{t('subtitle')}</Text.BodyText3>
        </div>

        {/* Decorative 3-step overview of what onboarding involves. Icons sit in
            their own row, connected by dashed separators (per the Figma); the
            title/description sit in a separate row underneath so the dashes
            only ever run between the icon circles, never through the copy.
            Both rows share the same three flex:1 / gap columns so each icon
            centers directly above its caption; the connectors are absolutely
            positioned lines drawn between the icon circles of columns 2 and 3. */}
        <div
          className={`d-flex flex-column ${css({
            width: '100%',
            gap: '12px',
            marginTop: '24px',
          })}`}
        >
          <div
            className={`d-flex ${css({
              width: '100%',
              gap: `${COLUMN_GAP_PX}px`,
            })}`}
          >
            {PREVIEW_STEPS.map((step, index) => (
              <div
                key={step.titleKey}
                className={`d-flex justify-content-center ${css({
                  flex: 1,
                  position: 'relative',
                })}`}
              >
                {index > 0 && (
                  <div
                    aria-hidden="true"
                    data-testid="quick-start-step-connector"
                    className={css({
                      position: 'absolute',
                      top: `${STEP_ICON_SIZE_PX / 2}px`,
                      left: `calc(-50% + ${
                        STEP_ICON_SIZE_PX / 2 + 8 - COLUMN_GAP_PX
                      }px)`,
                      right: `calc(50% + ${STEP_ICON_SIZE_PX / 2 + 8}px)`,
                      borderTop: `2px dashed ${merchantTheme.colors.spearmint30}`,
                    })}
                  />
                )}
                <div
                  className={`d-flex align-items-center justify-content-center ${css(
                    {
                      width: `${STEP_ICON_SIZE_PX}px`,
                      height: `${STEP_ICON_SIZE_PX}px`,
                      borderRadius: `${STEP_ICON_SIZE_PX / 2}px`,
                      border: `2px solid ${merchantTheme.colors.spearmint50}`,
                      flexShrink: 0,
                    }
                  )}`}
                >
                  <i
                    className={`fa ${step.icon} ${css({
                      fontSize: '20px',
                      color: merchantTheme.colors.spearmint50,
                    })}`}
                    aria-hidden="true"
                  />
                </div>
              </div>
            ))}
          </div>

          <div
            className={`d-flex justify-content-between ${css({
              width: '100%',
              gap: `${COLUMN_GAP_PX}px`,
            })}`}
          >
            {PREVIEW_STEPS.map(step => (
              <div
                key={step.titleKey}
                className={`d-flex flex-column align-items-center ${css({
                  flex: 1,
                  gap: '8px',
                })}`}
              >
                <Text.BodyText3 bold>{t(step.titleKey)}</Text.BodyText3>
                <Text.BodyText4 color="grey70">
                  {t(step.descKey)}
                </Text.BodyText4>
              </div>
            ))}
          </div>
        </div>

        {/* ~5 minute note (teal styled inline, mirroring the stepper accent). */}
        <div
          className={`d-flex align-items-center ${css({
            gap: '8px',
            marginTop: '8px',
            color: merchantTheme.colors.spearmint50,
          })}`}
        >
          <i className="fa fa-clock-o" aria-hidden="true" />
          <span className={css({ fontWeight: 700, fontSize: '14px' })}>
            {t('minutes_note')}
          </span>
        </div>

        <Button
          kind="primary"
          onClick={() => dispatch(push(buildConfigurePath('train')))}
          endEnhancer={() => (
            <i className="fa fa-arrow-right" aria-hidden="true" />
          )}
        >
          {t('get_started')}
        </Button>
      </div>
    </div>
  )
}

export default QuickStart
